import type { Experiment, Result } from '@growthbook/growthbook';
import type { GrowthBookVersionsModel } from '~/common/models/versions.model.ts';

export function useGrowthBook() {
  const { QUIZ_VER, LANDING_VER, HOME_VER, PAYMENT_VER, UPSALE_VER, TRACK_AFTER } = useRuntimeConfig().public;

  const {
    $initGrowthBook,
    $db,
    $setVersions,
    $getQuiz,
    $mixpanel,
  } = useNuxtApp();
  const { mixpanelTrack } = useMixpanel();
  const route = useRoute();
  const expId = ref<string>();
  const variationName = ref<string>();
  const growthBookIncstance = ref<any>();
  const store = useKegelStore();

  async function trackingCallback(experiment: Experiment<any>, result: Result<any>): Promise<void> {
    const savedExperiment = await $db.getItem('experiment');
    // eslint-disable-next-line no-console
    console.log('Experiment Started', experiment?.name, result?.name);
    // eslint-disable-next-line no-console
    console.log('Saved Experiment', savedExperiment?.experimentId, 'query', route.query.experimentId, route.query.variation);
    if (savedExperiment?.experimentId === experiment?.name || experiment?.name === route.query.experimentId) {
      expId.value = savedExperiment?.experimentId || route.query.experimentId;
      variationName.value = savedExperiment?.variation || route.query.variation;
      await setVersions();
      return;
    }
    expId.value = experiment.name;
    variationName.value = result.name;
    const versions: GrowthBookVersionsModel = {
      paymentForm: growthBookIncstance.value?.getFeatureValue('paymentForm', null),
      Quiz: growthBookIncstance.value?.getFeatureValue('Quiz', null),
      StartScreen: growthBookIncstance.value?.getFeatureValue('StartScreen', null),
      landing: growthBookIncstance.value?.getFeatureValue('landing', null),
      upsale: growthBookIncstance.value?.getFeatureValue('upsale', null),
      trackOnlyAfterQuiz: growthBookIncstance.value?.getFeatureValue('trackOnlyAfterQuiz', null),
    };
    versions[String(result.featureId)] = result.value as string;

    await setVersions(versions);
    mixpanelTrack('$experiment_started', {
      [experiment.name as string]: result.name,
      'Experiment name': experiment.key,
      'Variant name': result.variationId,
      '$source': 'growthBook',
    });
  }
  async function initGrowthBook(): Promise<void> {
    const cmpId = route.query.cmpid as string;
    const growthBook = await $initGrowthBook(
      trackingCallback,
      cmpId,
      store.userUuid,
      $mixpanel?.get_distinct_id(),
    );
    growthBookIncstance.value = growthBook;
    growthBook?.isOn('landing');
    growthBook?.isOn('paymentForm');

    if (!cmpId || !expId.value) {
      await setVersions();
    }

    if (growthBook && cmpId) {
      $db.setItem('cmpId', cmpId as string);
    }
  }

  async function setVersions(featureValue?: GrowthBookVersionsModel): Promise<void> {
    const BASE_LINE_VERSION = '1';
    const {
      paymentForm,
      quiz,
      homePage,
      landing,
      upsale,
      trackOnlyAfterQuiz,
    } = await $db.getItem('versions') || {};

    // featureValue KEY is features name such us paymentForm, Quiz and etc from GrowthBook features naming space
    const versions = {
      paymentForm: String(featureValue?.paymentForm || paymentForm || PAYMENT_VER || BASE_LINE_VERSION),
      quiz: String(featureValue?.Quiz || quiz || QUIZ_VER || BASE_LINE_VERSION),
      homePage: String(featureValue?.StartScreen || homePage || HOME_VER || BASE_LINE_VERSION),
      landing: String(featureValue?.landing || landing || LANDING_VER || BASE_LINE_VERSION),
      upsale: String(featureValue?.upsale || upsale || UPSALE_VER || BASE_LINE_VERSION),
      trackOnlyAfterQuiz: String(featureValue?.trackOnlyAfterQuiz || trackOnlyAfterQuiz || TRACK_AFTER || BASE_LINE_VERSION),
    };

    const result = await $setVersions(versions);

    await $getQuiz(result.quiz);
  }

  const experimentId = computed(() => {
    return expId.value;
  });

  const variation = computed(() => {
    return variationName.value;
  });
  return {
    initGrowthBook,
    experimentId,
    variation,
  };
}
